import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import logoFallback from "@/assets/allen-travels-logo.png";
import { cn } from "@/lib/utils";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "Tours", href: "/tours" },
  { label: "Transfers", href: "/transfers" },
  { label: "Destinations", href: "/destinations" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { getSetting } = useSiteSettings();
  const logoUrl = getSetting("header_logo_url", "") || logoFallback;
  const phone = getSetting("phone", "");
  const telHref = `tel:${phone.replace(/[^+\d]/g, "")}`;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const transparent = isHome && !scrolled;

  return (
    <header
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-smooth",
        transparent
          ? "bg-transparent"
          : "bg-background/85 backdrop-blur-xl border-b border-border/60 shadow-soft",
      )}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={logoUrl}
              alt="Allen Travels RD"
              className="h-12 w-auto transition-smooth group-hover:scale-105"
            />
            <span
              className={cn(
                "hidden sm:flex flex-col leading-tight font-display font-semibold tracking-tight",
                transparent ? "text-white" : "text-foreground",
              )}
            >
              <span className="text-base">Allen Travels</span>
              <span className="text-[10px] uppercase tracking-[0.25em] opacity-70">RD</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.href}
                to={l.href}
                end={l.href === "/"}
                className={({ isActive }) =>
                  cn(
                    "relative px-4 py-2 text-sm font-medium rounded-full transition-smooth",
                    transparent
                      ? "text-white/85 hover:text-white"
                      : "text-foreground/75 hover:text-foreground",
                    isActive && (transparent ? "text-white" : "text-primary"),
                    isActive && "after:content-[''] after:absolute after:left-1/2 after:-translate-x-1/2 after:bottom-1 after:w-1 after:h-1 after:rounded-full after:bg-current",
                  )
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
          {phone && (
            <a
              href={telHref}
              className={cn(
                "flex items-center gap-2 text-sm font-medium",
                transparent ? "text-white/90 hover:text-white" : "text-foreground/80 hover:text-primary",
              )}
            >
              <Phone className="w-4 h-4" />
              {phone}
            </a>
          )}
            <Link
              to="/transfers"
              className="px-5 py-2.5 rounded-full bg-gradient-ocean text-primary-foreground text-sm font-semibold shadow-glow hover:shadow-elegant transition-smooth hover:-translate-y-0.5"
            >
              Book Now
            </Link>
          </div>

          <button
            className={cn(
              "lg:hidden p-2 rounded-md transition-smooth",
              transparent ? "text-white" : "text-foreground",
            )}
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-background/95 backdrop-blur-xl border-t border-border/60">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.href}
                to={l.href}
                end={l.href === "/"}
                className={({ isActive }) =>
                  cn(
                    "px-4 py-3 rounded-lg text-base font-medium transition-smooth",
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-foreground/80 hover:bg-muted",
                  )
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/transfers"
              className="mt-2 px-4 py-3 rounded-lg bg-gradient-ocean text-primary-foreground font-semibold text-center"
            >
              Book Now
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
